import React, { useContext, useEffect, useState, useRef } from 'react';
import { Row, Col, Spin, Button  } from "antd";
import { 
    LoadingOutlined, 
    CloseOutlined, 
    RightSquareFilled, 
    LeftSquareFilled, 
    SnippetsFilled, 
    FileTextFilled, 
    CloseCircleOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import { AppContext } from '../../../util/context';
import { ActionLogNotification } from '../../ActionTracker/ActionLogNotification';
import { uploadData } from 'aws-amplify/storage';
import dayjs from 'dayjs';
import { listHZDCardWithAll } from '../../../util/myQueries';
import { GeneralInformationDetails } from './GeneralInformationDetails';
import { ActionDetails } from './ActionDetails';
import { CardOverview } from './CardOverview';
import { createHazardActionLog, createHazardActions, updateHazard, updateHazardActions } from '../../../graphql/mutations';
import { ActionLog } from '../../ActionTracker/ActionLog';
import { enableDaysLimit, cleanDepartment } from '../../../util/details';


const sections = ["", "GENERAL INFORMATION", "ACTIONS", "CARD OVERVIEW"];
export const HazardDetailsView = ({
    setDetailsViewModal,
    detailsViewModal,
    client,
    isRep,
    viewOnly,
    assignedView,
    actionID,
    setRefetchData,
    refetchData
}) => {
    const {
        StyledDiv,
        userData,
        allEmployeeUsers
    } = useContext(AppContext)
    const actionLogDataRef = useRef();
    const actionDataRef = useRef();
    const scrollableContainerRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [hazardCard, setHazardCard] = useState();
    const [currentSection, setCurrentSection] = useState(1);
    const [error, setError] = useState({show: false, message: ""});
    const [saveSuccess, setSaveSuccess] = useState({show: false, message: "", type: ''});
    const [detailsRefetchData, setDetailsRefetchData] = useState(false);
    const [closeCard, setCloseCard] = useState(false);
    const [assignedCanView, setAssignedCanView] = useState(false)
    const [logValidationErrors, setLogValidationErrors] = useState({
        dueDate: "", 
        assignedTo: "",
        comments: ""
    });

    const [showActionLog, setShowActionLog] = useState(false);

    const [category, setCategory] = useState("");
    const [selectedCategoryDetails, setSelectedCategoryDetails] = useState(null);


    useEffect(() => {
        let mounted = true;

        const getData = async () => {
            try{
                const getHazardCard = await client.graphql({
                    query: listHZDCardWithAll,
                    variables: { id: detailsViewModal.id }
                })

                if(getHazardCard) {
                    
                    const actionData = getHazardCard.data.getHazard.actions.items;
                    let actions = []
                    if(actionData){
                        actions = actionData.map(action => ({
                            ...action,
                            logs: {
                                ...action.logs,
                                items: action.logs ? action.logs.items.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt)) : []
                            }
                        }));


                    }

    
                    const newHZDCardWN = {...getHazardCard.data.getHazard, actions: actions};

                    if (isRep && newHZDCardWN.status !== 'closed' && newHZDCardWN.status !== 'void') {
                        setCloseCard(newHZDCardWN.actions.every(action => action.status === 'closed' || action.status === 'void'));
                    } else {
                        setCloseCard(false);
                    }

                    const canView = newHZDCardWN.actions.length > 0 && newHZDCardWN.actions.some(action => action.assignedTo === userData.given_name + " " + userData.family_name && ( action.status === 'assigned' || action.status === 'reassigned'))
                    setAssignedCanView(canView)

                    setHazardCard(newHZDCardWN);

                    if(actionID){
                        setShowActionLog(true);
                    }
                }
            }catch(error){
                console.log('Error fetching data:', error);
                setError({show: true, message: "Error Fetching Data"});
            } finally {
                setLoading(false);
            }
        }

        if (mounted) {
            getData();
        }

        return () => {
            mounted = false;
        }
    },[detailsViewModal.id, detailsRefetchData, actionID, client, isRep, userData])

    const handleSuccessClose = () => {
        setLoading(true)
        if(saveSuccess.type === "card"){
            setCurrentSection(1)
            scrollToTop()
            setSaveSuccess({show: false, message: "", type: ""})
        }

        if(saveSuccess.type === "action"){
            setSaveSuccess({show: false, message: "", type: ""})
        }


        if(saveSuccess.type === "cardClosed" || saveSuccess.type === "cardVoid"){
            setRefetchData(!refetchData)
            setDetailsViewModal({show: false, id: undefined, actionID: undefined})
        }

        setDetailsRefetchData(!detailsRefetchData)
        
        
    }

    const handlePreviousSection = () => {
        setCurrentSection(section => section - 1);
        scrollToTop();
    };

    const handleNextSection = () => {

        setCurrentSection(currentSection => currentSection + 1);

        scrollToTop();
    };

    const handleUpdateAction = () => {
        /* 
            This function is called when the user clicks the update action button
            It checks if the action status has been changed and updates accordingly
            If the status was changed and the new status is either assigned or reassigned then it checks if the assignedTo and dueDate fields are filled

        */
        const currentAction = actionDataRef.current.getActionData()
        const newAction = actionLogDataRef.current.getActionDetails()
        let validated = true
        if(newAction.status === 'assigned' || newAction.status === 'reassigned'){
            let assignedToError = "", dueDateError = "", commentsError = "";
            if(newAction.assignedTo === "" || newAction.assignedTo === null){
                assignedToError = "Assigned To is required"
                validated = false
            }

            if(newAction.dueDate === "" || newAction.dueDate === null){
                dueDateError = "Due Date is required"
                validated = false
            }else{
                let today = dayjs();
                let dueDate = newAction.defaultDueDate ? newAction.defaultDueDate : dayjs(currentAction.dueDate, "YYYY-MM-DD");
                
                if (dueDate.isBefore(today, 'day')) {
                    dueDateError = "Due Date cannot be in the past";
                    validated = false;
                }

                
                const currentYear = dayjs().year();
                if (dueDate.year() > currentYear + 1) {
                    dueDateError = "Due Date cannot be later than next year";
                    validated = false;
                }

                if(enableDaysLimit){
                    let sixtyDaysFromNow = today.add(60, 'days');
                    if (dueDate.isAfter(sixtyDaysFromNow, 'day')) {
                        dueDateError = "Due Date cannot be more than 60 days from today";
                        validated = false;
                    }
                }
            }

            if(newAction.status === 'reassigned' && newAction.comments === ""){
                commentsError = "Comments are required when reassigning an action"
                validated = false
            }

            setLogValidationErrors({
                assignedTo: assignedToError,
                dueDate: dueDateError,
                comments: commentsError
            })
        }


        if(newAction.status === 'closed'){
            if((currentAction.assignedTo === '' || currentAction.assignedTo === null) && (newAction.assignedTo === '' || newAction.assignedTo === null)){
                setLogValidationErrors({
                    assignedTo: "Assigned To is required",
                    dueDate: "",
                    comments: ""
                })
                validated = false
            }
        }

        if(newAction.status === 'void'){
            if(newAction.comments === ""){
                setLogValidationErrors({
                    assignedTo: "",
                    dueDate: "",
                    comments: "Comments are required"
                })
                validated = false
            }
        }
        
        if(validated){
            setLoading(true);
            updateActionData(currentAction, newAction);
        }
    }

    async function updateActionData(currentAction, newAction) {

        /* 
            This function first checks if the status, assignedTo or dueDate fields have been changed for the action
            If they have been changed then it updates the action
            If the action was updated then the Hazard status is updated to assigned
            It then checks the new status of the action and sets the actionTaken variable accordingly which is used for the action log
            It then calls the handleCommentAndFiles function to handle the comments and files for the action
        */
        
        let updatedAction = null
        let actionTaken = ""
        try{
            if(newAction.status !== currentAction.status || newAction.assignedTo !== currentAction.assignedTo || newAction.dueDate !== currentAction.dueDate){
                let sendEmail = true

                if((newAction.status === currentAction.status) && (newAction.status === 'completed' || newAction.status === 'closed')){
                    sendEmail = false
                }
                
                const newActionState = {
                    id: currentAction.id,
                    status: newAction.status,
                    _version: currentAction._version,
                    assignedTo: newAction.assignedTo,
                    businessUnit: cleanDepartment(newAction.businessUnit),
                    dueDate: newAction.dueDate,
                    originalDueDate: newAction.defaultDueDate,
                    assignedToEmail: newAction.assignedToEmail,
                    sendEmail: sendEmail,
                    ...(newAction.status === 'void' && { voidJustification: newAction.comments })
                }

                updatedAction = await client.graphql({
                    query: updateHazardActions,
                    variables: { input: newActionState }
                });

                if(hazardCard.status !== 'assigned'){
                    const newHazardUpdate = {
                        id: hazardCard.id,
                        status: "assigned",
                        _version: hazardCard._version
                    }
        
                    await client.graphql({
                        query: updateHazard,
                        variables: { input: newHazardUpdate }
                    })
                    
                }

                switch (newAction.status) {
                    case "assigned":
                        if (currentAction.status === "completed") {
                            actionTaken = `Action Assigned to ${newAction.assignedTo}`;
                        } else if (["closed", "void"].includes(currentAction.status)) {
                            actionTaken = `Action Reopened and Assigned to ${newAction.assignedTo}`;
                        } else if(newAction.status !== currentAction.status) {
                            actionTaken = `Action Assigned to ${newAction.assignedTo}`;
                        }
                        break;
                    case "reassigned":
                        if (currentAction.status === "completed") {
                            actionTaken = `Action Reassigned to ${newAction.assignedTo}`;
                        } else if (["closed", "void"].includes(currentAction.status)) {
                            actionTaken = `Action Reopened and Reassigned to ${newAction.assignedTo}`;
                        } else if(newAction.status !== currentAction.status){
                            actionTaken = `Action Reassigned to ${newAction.assignedTo}`;
                        }
                        break;
                    case "closed":
                        actionTaken = "Action Closed";
                        break;
                    case "void":
                        actionTaken = "Action Voided";
                        break;
                    default:
                        break;
                }

            }
            

            if (updatedAction === null) {
                updatedAction = currentAction
            }else{
                updatedAction = updatedAction.data.updateHazardActions
            }

            await handleCommentAndFiles(updatedAction, newAction, actionTaken)
        }catch(error){
            setError({show: true, message: "Error Updating Action"});
            console.log(error)
        }finally{
            setLoading(false)
        }
    }

    async function handleCommentAndFiles(updatedAction, newAction, actionTaken) {
        /* 
            This function is called after the action has been updated
            It checks if there are any comments or files for the action
            If there are then it creates a new action log with the comments
            If there are files then it uploads the files to the S3 bucket and the linked function updates the table with the file paths       
        */
        let createdActionLog;
        
        try{
            if (newAction.comments !== "" || newAction.files.length > 0) {
                let logDetails = actionTaken;
                if (newAction.comments !== "") {
                    if(logDetails !== "")
                        logDetails += `\n${newAction.comments}`;

                    else
                        logDetails = `${newAction.comments}`;
                }
                const newActionLog = {
                    hazardActionsLogsId: updatedAction.id,
                    ACTID: updatedAction.id,
                    logDetails: logDetails,
                    personnelName: userData.given_name + " " + userData.family_name
                };
        
                createdActionLog = await client.graphql({
                    query: createHazardActionLog,
                    variables: { input: newActionLog }
                });
            }
            
            else if(actionTaken !== ""){
                let logDetails = actionTaken;
                const newActionLog = {
                    hazardActionsLogsId: updatedAction.id,
                    ACTID: updatedAction.id,
                    logDetails: logDetails,
                    personnelName: userData.given_name + " " + userData.family_name
                };
        
                createdActionLog = await client.graphql({
                    query: createHazardActionLog,
                    variables: { input: newActionLog }
                });
            }
        
            if (newAction.files.length > 0 && createdActionLog) {
                const actionLogId = createdActionLog.data.createHazardActionLog.id;
                let fileNameCounter = 0;
                const uploadedFiles = [];
        
                const savedFilesPromises = newAction.files.map(async (file) => {
                    const fileExtension = file.name.split('.').pop();
                    const timestamp = Date.now();
                    const baseFileName = sanitizeFilename(file.name)
                    const fileName = `hazardActionLogFiles/${actionLogId}/${baseFileName}_${timestamp}.${fileExtension}`;
                    
                    try {
                        const result = await uploadData({
                            key: fileName,
                            data: file,
                            options: { level: 'protected' }
                        }).result;

                        //console.log('Upload succeeded:', result);
                        uploadedFiles.push(fileName);
                        fileNameCounter = fileNameCounter + 1;
                    } catch (error) {
                        console.log('Upload error:', error);
                    }
                    return uploadedFiles;
                });
        
                await Promise.all(savedFilesPromises);
            }

            setSaveSuccess({show: true, message: "Action successfully updated", type: "action"})
        }catch(error){
            setError({show: true, message: "Error Updating Action Log and files"});
            console.log(error)
        }

        
    }

    function sanitizeFilename(filename, maxLength = 150) {
        const unsafeChars = /[^a-zA-Z0-9._-]/g;
    
        const extensionIndex = filename.lastIndexOf('.');
    
        const baseName = filename.substring(0, extensionIndex);
    
        let sanitizedBaseName = baseName.replace(/ /g, '_').replace(unsafeChars, '');
    
        if (sanitizedBaseName.length > maxLength) {
            sanitizedBaseName = sanitizedBaseName.substring(0, maxLength);
        }
    
        return sanitizedBaseName;
    }

    const handleSaveClick = () => {
        setLoading(true);
        saveHseData();
    }

    async function saveHseData(){
        /* 
            this function allows for the ability to save changes made to the hazard
            changes can be in the immediate action taken, immediate action taken details, further action required and actions
            if changes are made to any but the actions then updateHazardRequired is set to true which signifies that the hazard card needs to be updated
            if changes are made to the actions then updateActionsRequired is set to true which signifies that the actions need to be updated or created
        */
        const mutationPromises = [];

        try{

            if(hazardCard.updateHazardRequired){
                const newHazardUpdate = {
                    id: hazardCard.id,
                    _version: hazardCard._version,
                    observationCategory: hazardCard.observationCategory,
                    immediateActionTaken: hazardCard.immediateActionTaken,
                    immediateActionTakenDetails: hazardCard.immediateActionTaken === 'no' ? " " : hazardCard.immediateActionTakenDetails,
                    furtherActionRequired: hazardCard.furtherActionRequired,
                    observationType: hazardCard.observationType,
                    observationDescription: hazardCard.observationDescription,
                    businessUnit: hazardCard.businessUnit,
                    location: hazardCard.location,
                    status: "assigned"
                }
                mutationPromises.push(
                    client.graphql({
                        query: updateHazard,
                        variables: { input: newHazardUpdate }
                    })
                );
            }

            if(hazardCard.updateActionsRequired) {
                const newActions = hazardCard.actions.filter(action => action.update === "add");
                const editActions = hazardCard.actions.filter(action => action.update === "edit");

                if(editActions.length > 0){
                    editActions.forEach(action => {
                        const newAction = {
                            id: action.id,
                            action: action.action,
                            _version: action._version,
                        }

                        mutationPromises.push(
                            client.graphql({
                                query: updateHazardActions,
                                variables: { input: newAction }
                            })
                        );
                    });
                }

                if(newActions.length > 0){
                    newActions.forEach(action => {
                        const newAction = {
                            HZDID: hazardCard.id,
                            hazardActionsId: hazardCard.id,
                            action: action.action,
                            status: "open",
                            sendEmail: false
                        }

                        mutationPromises.push(
                            client.graphql({
                                query: createHazardActions,
                                variables: { input: newAction }
                            })
                        );
                    });
                }
            }

            await Promise.all(mutationPromises);
            setSaveSuccess({show: true, message: "Card successfully updated", type: "card"})
        }catch(error){
            console.log(error)
            setError({show: true, message: "Error Saving Data"})
        } finally{
            setLoading(false)
        }
    }

    const handleCardOverviewSelection = () => {
        setCurrentSection(3);
        setShowActionLog(false);
    }

    const handleAssignedUpdateAction = () => {
        /* reload page if card status is closed or void */
        const currentAction = actionDataRef.current.getActionData()
        const newAction = actionLogDataRef.current.getActionDetails()
        let validated = true, commentsError = "";
        if(newAction.comments === ''){
            commentsError = "Action Taken is required"
            validated = false
        }

        if(validated){
            setLoading(true);
            updateAssignedActionData(currentAction, newAction);
        }else{
            setLogValidationErrors({
                comments: commentsError,
                dueDate: "",
                assignedTo: ""
            })
        }
    }

    async function updateAssignedActionData(currentAction, newAction) {
        try{
            await handleCommentAndFiles(currentAction, newAction, "")
        }catch(error){
            setError({show: true, message: "Error Updating Action"});
            console.log(error)
        }finally{
            setLoading(false)
        }
    }

    const handleCloseAction = () => {
        /* reload page if card status is closed or void */
        const currentAction = actionDataRef.current.getActionData()
        const newAction = actionLogDataRef.current.getActionDetails()
        let validated = true, commentsError = "";
        if(newAction.comments === ''){
            commentsError = "Action Taken is required"
            validated = false
        }

        if(validated){
            setLoading(true);
            closeAction(currentAction, newAction);
        }else{
            setLogValidationErrors({
                comments: commentsError
            })
        }
       
    }

    const getCurrentDate = () => {
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    async function closeAction(currentAction, newAction) {
        let updatedAction = null
        try{
            const newActionState = {
                id: currentAction.id,
                status: "completed",
                completedDate: getCurrentDate(),
                _version: currentAction._version,
                sendEmail: true
            }

            updatedAction = await client.graphql({
                query: updateHazardActions,
                variables: { input: newActionState }
            });

            updatedAction = updatedAction.data.updateHazardActions

            if (updatedAction) {
                await handleCommentAndFiles(updatedAction, newAction, "Action Completed")
            }
        }catch (error) {
            setError({show: true, message: "Error Updating Action"});
            console.log("Error updating action", error)
        }finally{
            setLoading(false)
        }
    }

    const scrollToTop = () => {
        if (scrollableContainerRef.current) {
            scrollableContainerRef.current.scrollTop = 0;
        }
    };

    return (
        <div className="modal-overlay">
            {closeCard && <ActionLogNotification handleCardOverviewSelection={handleCardOverviewSelection}/>}
            
            <Row
                style={{
                    paddingTop: "20px",
                    paddingBottom: "20px",
                    height: "100dvh",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "start"
                }}
            >
                <Col 
                
                    xl={{span: 22}}
                    lg={{span: 22}}
                    md={{span: 22}}
                    sm={{span: 23}}
                    xs={{span: 23}}
                    style={{height: "94dvh",}}
                >
                    <div className="modal"
                        style={{
                            padding: "0",
                            height: "100%"
                        }}
                    >
                        {!loading && !error.show && !saveSuccess.show && <>
                            <Row
                                style={{
                                    height: "7%",
                                    backgroundColor: "#FFC000",
                                    borderTopLeftRadius: "10px",
                                    borderTopRightRadius: "10px",
                                }}
                            >
                                <Col
                                    span={22}
                                    style={{
                                        display: "flex",
                                        justifyContent: "start",
                                        alignItems: "center",
                                        paddingLeft: "10px"
                                    }}
                                >
                                    <StyledDiv
                                        className="homeDetailsText"
                                    >
                                        {!showActionLog && sections[currentSection]}
                                        {showActionLog && "ACTION LOG"}
                                    </StyledDiv>

                                    <StyledDiv
                                        className="homeReviewText"
                                    >
                                        {isRep && "QHSSE REVIEW"}
                                    </StyledDiv>

                                    <StyledDiv
                                        className="homeReviewText"
                                    >
                                        {hazardCard.HZDID.toUpperCase()}
                                    </StyledDiv>
                                </Col>

                                <Col
                                    span={2}
                                    style={{
                                        display: "flex",
                                        justifyContent: "end",
                                        alignItems: "center",
                                        paddingRight: "10px"
                                    }}
                                >
                                    <CloseOutlined 
                                        style={{
                                            color: "white",
                                            fontSize: "20px",
                                            cursor: "pointer"
                                        }}
                                        onClick = {() => {
                                            if(setRefetchData)
                                                setRefetchData(!refetchData); 
                                            setDetailsViewModal({show: false, id: undefined, actionID: undefined})
                                        }}
                                    />
                                </Col>

                            </Row>

                            {!showActionLog && <>
                                <Row
                                    style={{
                                        height: "83%",
                                        overflowY: "auto",
                                        overflowX: "hidden",
                                    }}
                                    ref={scrollableContainerRef}
                                >
                                    <Col span={24}
                                        style={{
                                            padding: "20px",
                                        }}
                                    >
                                        {currentSection === 1 && <>
                                            <GeneralInformationDetails 
                                                hazardCard = {hazardCard}
                                                setHazardCard = {setHazardCard}
                                                setCategory = {setCategory}
                                                category = {category}
                                                setSelectedCategoryDetails = {setSelectedCategoryDetails}
                                                selectedCategoryDetails = {selectedCategoryDetails}
                                                isRep = {isRep}
                                            />
                                        </>}

                                        {currentSection === 2 && <>
                                            <ActionDetails 
                                                hazardCard = {hazardCard} 
                                                setHazardCard={setHazardCard} 
                                                isRep={isRep}
                                            />
                                        </>}

                                        {currentSection === 3 && <CardOverview 
                                            handleSaveClick={handleSaveClick}
                                            setLoading={setLoading}
                                            hazardCard={hazardCard}
                                            setError={setError}
                                            setSaveSuccess={setSaveSuccess}
                                            client={client}
                                            closeCard={closeCard}
                                            getCurrentDate={getCurrentDate}
                                            category={category}
                                        />}
                                    </Col>
                                </Row>
                            </>}
                            

                            {showActionLog && <>
                                <ActionLog
                                    data={hazardCard} 
                                    StyledDiv={StyledDiv}
                                    isRep={isRep}
                                    viewOnly={viewOnly}
                                    assignedView={assignedView}
                                    actionLogDataRef={actionLogDataRef}
                                    closeCard={closeCard}
                                    actionID={actionID}
                                    logValidationErrors={logValidationErrors}
                                    setLogValidationErrors={setLogValidationErrors}
                                    allEmployees={allEmployeeUsers}
                                    hazardMode={true}
                                    ref={actionDataRef}
                                />
                            </>}

                            <Row
                                style={{
                                    height: "10%",
                                }}
                            >
                                <Col
                                    span={10}
                                    style={{
                                        display: "flex",
                                        justifyContent: "start",
                                        alignItems: "center",
                                        paddingLeft: "20px"
                                    }}
                                >
                                    {!showActionLog && currentSection > 1 && <LeftSquareFilled className="homeButtonIcon" onClick={() => handlePreviousSection()}/>}
                                    {(hazardCard.status !== 'closed' && hazardCard.status !== 'void') && showActionLog && <>
                                        {!viewOnly && !isRep && assignedView && assignedCanView && <Button
                                            onClick = {() => handleAssignedUpdateAction()}
                                        >Update Action</Button>}

                                        
                                    </>}
                                </Col>

                                <Col
                                    span={4}
                                    style={{
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                    }}
                                >   
                                    {(viewOnly || assignedView || isRep) && <>
                                        {!showActionLog && <div
                                            className="actionLogContainer"
                                            onClick={() => setShowActionLog(true)}
                                        >
                                            <SnippetsFilled className="actionLogIcon" style={{
                                                marginRight: "5px"
                                            }}/>
                                            <StyledDiv
                                                className="actionLogText"
                                            >
                                                Action Log
                                            </StyledDiv>
                                        </div>}

                                        {showActionLog && <div
                                            className="actionLogContainer"
                                            onClick={() => setShowActionLog(false)}
                                        >
                                            <FileTextFilled className="actionLogIcon" style={{
                                                marginRight: "5px"
                                            }}/>
                                            <StyledDiv
                                                className="actionLogText"
                                            >
                                                Card Data
                                            </StyledDiv>
                                        </div>}
                                    </>}
                                </Col>

                                <Col
                                    span={10}
                                    style={{
                                        display: "flex",
                                        justifyContent: "end",
                                        alignItems: "center",
                                        paddingRight: "20px"
                                    }}
                                >
                                    {!showActionLog && currentSection < 3  && (currentSection !== 2 || isRep) && <RightSquareFilled className="homeButtonIcon" onClick={() => handleNextSection()}/>}

                                    {(hazardCard.status !== "closed" && hazardCard.status !== 'void') && showActionLog && !viewOnly && !isRep && assignedView && assignedCanView && <>
                                        <Button
                                            onClick={() => handleCloseAction()}
                                        >Complete Action</Button>
                                    </>}
                                    
                                    {(hazardCard.status !== 'closed' && hazardCard.status !== 'void') && showActionLog && <>
                                        {!viewOnly && isRep && !assignedView && <Button
                                                onClick = {() => handleUpdateAction()}
                                            >Update Action</Button>
                                        }
                                    </>}
                                </Col>
                            </Row>
                        </>}

                        
                        {loading && <Row
                            style={{
                                height: "100%",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                        >
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                                <Spin indicator={<LoadingOutlined style={{ fontSize: 50 }} spin />} spinning={loading}></Spin>
                                <StyledDiv>
                                    <p
                                        style={{
                                            fontSize: "16px",
                                            color: "rgb(174,32,114)",
                                            marginTop: "40px"
                                        }}
                                    >Please Wait...</p>
                                </StyledDiv>
                            </div>
                        </Row>}

                        {!loading && error.show && (
                            <Row
                                style={{
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    
                                }}
                            >
                                <CloseCircleOutlined style={{ fontSize: 30, color:"red", marginBottom: "10px" }} />
                                <StyledDiv
                                    className="homeErrorText"
                                >{error.message}</StyledDiv>
                                <Button
                                    style={{
                                        marginTop: "80px"
                                    }}
                                    onClick={() => {
                                        setDetailsViewModal({show: false, id: undefined});
                                    }}
                                >Close</Button>
                            </Row>
                        )}

                        {!loading && saveSuccess.show && <>
                            <Row
                                style={{
                                    height: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    
                                }}
                            >
                                <CheckCircleOutlined style={{ fontSize: 30, color:"#4CAF50", marginBottom: "10px" }} />
                                <StyledDiv
                                    className = "homeSuccessText"
                                >{saveSuccess.message}</StyledDiv>
                                <Button
                                    style={{
                                        marginTop: "80px"
                                    }}
                                    onClick={() => {
                                        handleSuccessClose()
                                    }}
                                >Close</Button>
                            </Row>
                        </>}
                    </div>
                </Col>
            </Row>
        </div>
    )
}
